import { type ChangeEvent, useEffect } from "react";
import { useFormikContext } from "formik";
import { Link } from "react-router-dom";
// @ts-expect-error pure js
import { ReactSelect } from "../../UI/Select";
// @ts-expect-error pure js
import TextInput from "../../UI/TextInput";
// @ts-expect-error pure js
import Radio from "../../UI/Radio";
// @ts-expect-error pure js
import RadioGroup from "../../UI/RadioGroup";
// @ts-expect-error pure js
import ZoneTendue from "./ZoneTendue";
import ZoneTendueCallout from "./ZoneTendueCallout";
// @ts-expect-error pure js
import useLeaseTemplates from "../../../hooks/use-lease-templates";
import Tooltip from "../../UI/Tooltip";
// @ts-expect-error pure js
import { Interrogation } from "../../UI/Icons";
// @ts-expect-error pure js
import Checkbox from "../../UI/Checkbox";
// @ts-expect-error pure js
import ExpandableCheckboxList from "../../UI/ExpandableCheckboxList";
import Card from "../../UI/Card";
import Callout from "../../UI/Callout";
import Title from "../../UI/Title";
import Text from "../../UI/Text";
import {
  DETAIL_CHARGES,
  DETAIL_CHARGES_PRO,
} from "../../../models/property.js";
// @ts-expect-error pure js
import FieldArrayAutreCharge from "../../Tenant/FieldArrayAutreCharge";
import { ChargesType, TenantLeaseType } from "../../../utils/constants/tenants";
// @ts-expect-error pure js
import { TVA_RATES } from "../../../utils/constants/compta";
import styles from "./Finance.module.css";

interface FinanceProps {
  edit?: boolean;
}

interface LeaseTemplateOption {
  id: number;
  nom: string;
  type: string;
  colocation?: boolean;
}

interface FinanceAppartement {
  nom?: string;
}

interface FinanceValues {
  id_lease_template?: string | number;
  type?: string;
  is_coloc?: boolean;
  is_immeuble?: boolean;
  appartements?: FinanceAppartement[];
  detail_charges: string[];
  zone_tendue?: string;
  tva?: boolean;
  tva_charge?: boolean;
  loyer_reference?: string;
}

const BAIL_COMMERCIAUX = [
  TenantLeaseType.COMMERCIAL,
  TenantLeaseType.COMMERCIAL_DEROGATOIRE,
  TenantLeaseType.PROFESSIONNEL,
];

const BAIL_CIVIL = [TenantLeaseType.CIVIL];

const BAIL_GARAGE = [TenantLeaseType.GARAGE, TenantLeaseType.STOCKAGE];

function Finance({ edit }: FinanceProps) {
  const { values, setFieldValue, handleChange } =
    useFormikContext<FinanceValues>();
  const { leaseTemplates } = useLeaseTemplates();

  const leaseTemplate = leaseTemplates.find(
    (lease: LeaseTemplateOption) =>
      parseInt(values.id_lease_template as string) === lease.id,
  );
  let filteredLeaseTemplates;
  if (values.type === "commercial") {
    filteredLeaseTemplates = leaseTemplates.filter(
      (template: LeaseTemplateOption) =>
        BAIL_CIVIL.concat(BAIL_COMMERCIAUX).includes(template.type),
    );
  } else if (values.type === "garage") {
    filteredLeaseTemplates = leaseTemplates.filter(
      (template: LeaseTemplateOption) => BAIL_GARAGE.includes(template.type),
    );
  } else {
    if (values.is_coloc) {
      filteredLeaseTemplates = leaseTemplates.filter(
        (template: LeaseTemplateOption) => template.colocation,
      );
    } else {
      filteredLeaseTemplates = leaseTemplates.filter(
        (template: LeaseTemplateOption) =>
          !BAIL_GARAGE.concat(BAIL_COMMERCIAUX).includes(template.type) &&
          !template.colocation,
      );
    }
  }

  const leaseTemplateOptions = filteredLeaseTemplates.map(
    (option: LeaseTemplateOption) => ({
      value: option.id,
      label: option.nom,
    }),
  );

  let detail_charges = [...DETAIL_CHARGES];
  if (leaseTemplates.habitation) {
    detail_charges = [...DETAIL_CHARGES, ...DETAIL_CHARGES_PRO];
  }

  const detailChargesCheckboxItems = [
    ...detail_charges,
    ...values.detail_charges.filter((i) => !detail_charges.includes(i)),
  ];

  function addItem(fieldName: string, item: string) {
    const current = values[fieldName as keyof FinanceValues] as
      | string[]
      | undefined
      | null;
    if (current === undefined || current === null) {
      setFieldValue(fieldName, [item]);
    } else if (!current.includes(item)) {
      setFieldValue(fieldName, [...current, item]);
    }
  }
  const lease = leaseTemplates.find(
    (template: LeaseTemplateOption) =>
      template.id === parseInt(values.id_lease_template as string),
  );

  const typeChargeProvisionForced =
    lease?.type !== TenantLeaseType.MEUBLE &&
    lease?.type !== TenantLeaseType.ETUDIANT;

  useEffect(() => {
    if (typeChargeProvisionForced) {
      setFieldValue("type_charge", ChargesType.PROVISION);
    }
  }, [leaseTemplates, setFieldValue, lease, typeChargeProvisionForced]);

  // La location d'habitation est exonérée de TVA : les cases sont retirées et
  // les valeurs purgées si le type de bien change en cours de formulaire.
  const isHabitation = values.type === "habitation";
  useEffect(() => {
    if (isHabitation && (values.tva || values.tva_charge)) {
      setFieldValue("tva", false);
      setFieldValue("taux_tva", "");
      setFieldValue("tva_charge", false);
      setFieldValue("taux_tva_charge", "");
    }
  }, [isHabitation, values.tva, values.tva_charge, setFieldValue]);

  return (
    <>
      {!values.is_immeuble && (
        <ReactSelect
          name="id_lease_template"
          label="Type de bail"
          mandatory
          className={styles.spacedBottom}
          options={leaseTemplateOptions}
        />
      )}
      {lease?.colocation &&
        (!lease?.collectif || lease?.type === TenantLeaseType.MOBILITE) &&
        !edit && (
          <>
            <Callout variant="warning">
              Dans le cadre d'une colocation avec baux individuels, pour
              l'abonnement, chaque chambre comptera comme un bien à part
              entière. Nous allons configurer toutes les chambres d'un coup,
              entrez le nombre de chambre que vous souhaitez créer dans le champ
              ci-dessous. Vous pourrez mettre un loyer et un nom différent pour
              chaque chambre après les avoir créés.
            </Callout>
            <TextInput
              name={"nb_rooms"}
              type={"number"}
              label={"Nombre de chambre à créer"}
            />
          </>
        )}
      {values.is_immeuble && (values.appartements?.length ?? 0) > 0 ? (
        <>
          {values.appartements?.map((_, index) => (
            <Card key={index} padding={"sm"} className={styles.spacedBottom}>
              <Title variant="hecto" className={styles.spacedBottom}>
                {values.appartements?.[index]?.nom ||
                  `Appartement ${String(index + 1).padStart(2, "0")}`}
              </Title>
              <ReactSelect
                name={`appartements[${index}].id_lease_template`}
                label="Type de bail"
                mandatory
                className={styles.spacedBottom}
                options={leaseTemplateOptions}
              />
              <div className={styles.gridCols}>
                <TextInput
                  type="number"
                  name={`appartements[${index}].nb_pieces_principales`}
                  placeholder="3"
                  unit={"pièces"}
                  label="Nombre de pièces"
                  mandatory
                />
                <TextInput
                  type="number"
                  name={`appartements[${index}].surface_total`}
                  placeholder="45"
                  step={"0.01"}
                  unit={"m²"}
                  label="Surface"
                  mandatory
                />
              </div>
              <div className={styles.gridCols}>
                <TextInput
                  type="number"
                  name={`appartements[${index}].loyer_hc`}
                  placeholder="380,10"
                  step={"0.01"}
                  unit={"€"}
                  label="Loyer hors charge"
                  mandatory
                />
                <TextInput
                  type="number"
                  name={`appartements[${index}].charges`}
                  placeholder="50"
                  step={"0.01"}
                  unit={"€"}
                  label="Charges"
                  mandatory
                />
                <TextInput
                  type="number"
                  name={`appartements[${index}].deposit`}
                  placeholder="350"
                  step={"0.01"}
                  unit={"€"}
                  label="Dépôt de garantie"
                  mandatory
                />
              </div>
            </Card>
          ))}
          <TextInput
            type="number"
            name="day_payment"
            placeholder="05"
            unit={"du mois"}
            mandatory
            label="Jour de paiement du loyer"
          />
        </>
      ) : (
        <>
          <div className={styles.gridCols}>
            <div>
              <TextInput
                type="number"
                name="loyer_hc"
                placeholder="380,10"
                step={"0.01"}
                label={`Loyer hors charge ${
                  values.zone_tendue === "true"
                    ? " (hors complément de loyer)"
                    : ""
                }`}
                unit={"€"}
                mandatory
              />
              {!isHabitation && (
                <>
                  <Checkbox
                    name={"tva"}
                    className={styles.spacedBottom}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleChange(e);
                      if (!e.target.checked) {
                        setFieldValue("taux_tva", "");
                      }
                    }}
                  >
                    Le loyer est soumis à la TVA
                  </Checkbox>
                  {values.tva && (
                    <ReactSelect
                      name="taux_tva"
                      label="Taux de TVA"
                      mandatory
                      options={TVA_RATES}
                      className={styles.spacedBottom}
                    />
                  )}
                </>
              )}
            </div>
            <div>
              <TextInput
                type="number"
                name="charges"
                placeholder="50"
                step={"0.01"}
                unit={"€"}
                label="Charges"
                mandatory
              />
              {!isHabitation && (
                <>
                  <Checkbox
                    name={"tva_charge"}
                    className={styles.spacedBottom}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleChange(e);
                      if (!e.target.checked) {
                        setFieldValue("taux_tva_charge", "");
                      }
                    }}
                  >
                    Les charges sont soumises à la TVA
                  </Checkbox>
                  {values.tva_charge && (
                    <ReactSelect
                      name="taux_tva_charge"
                      label="Taux de TVA sur les charges"
                      mandatory
                      options={TVA_RATES}
                      className={styles.spacedBottom}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <FieldArrayAutreCharge />
          <Text>Détail des charges facturées :</Text>
          <ExpandableCheckboxList
            name="detail_charges"
            items={detailChargesCheckboxItems}
            addItem={addItem}
            className={styles.checkboxList}
          />
          {values.type !== "commercial" && !typeChargeProvisionForced && (
            <RadioGroup>
              <Radio
                name="type_charge"
                value={ChargesType.PROVISION}
                label="Provision sur charge"
              />
              <Radio
                name="type_charge"
                value={ChargesType.FORFAIT}
                label="Forfait de charge"
              />
            </RadioGroup>
          )}
          <div className={styles.gridCols}>
            <TextInput
              type="number"
              name="deposit"
              placeholder="350"
              step={"0.01"}
              unit={"€"}
              label="Montant du dépôt de garantie"
              mandatory
            />

            <TextInput
              type="number"
              name="day_payment"
              placeholder="05"
              unit={"du mois"}
              mandatory
              label="Jour de paiement du loyer"
            />
          </div>
        </>
      )}
      {leaseTemplate?.type === TenantLeaseType.MOBILITE && (
        <Callout variant="danger" className={styles.errorCard}>
          Attention, il n'est pas autorisé de prendre un dépôt de garantie dans
          le cadre d'un bail mobilité. Cette valeur ne sera donc pas prise en
          compte lors de l'édition du bail.
        </Callout>
      )}
      {values.type === "habitation" && (
        <div className={styles.question}>
          <Text>
            Votre logement est il situé en zone tendue ?{" "}
            <Tooltip
              content={
                <Text>
                  Rendez-vous sur le{" "}
                  <Link
                    to={
                      "https://www.service-public.fr/simulateur/calcul/zones-tendues"
                    }
                    target={"_blank"}
                  >
                    simulateur du gouvernement
                  </Link>{" "}
                  pour savoir si votre bien est situé en zone tendue
                </Text>
              }
            >
              <Interrogation />
            </Tooltip>
          </Text>
          <div className={styles.gridCols}>
            <Radio
              name="zone_tendue"
              value="true"
              label="Zone tendue"
              dependon={"id_parent"}
            />
            <Radio
              name="zone_tendue"
              value="false"
              label="Zone non tendue"
              dependon={"id_parent"}
            />
          </div>
          <ZoneTendueCallout />
        </div>
      )}
      {(values.type === "commercial" || values.type === "garage") && (
        <>
          <Text>Indice d'indexation du loyer : </Text>
          <div className={styles.gridColsCompact}>
            {values.type === "garage" && (
              <Radio
                name="type_indice"
                value="IRL"
                label="IRL"
                dependon={"id_parent"}
              />
            )}
            <Radio
              name="type_indice"
              value="ILC"
              label="ILC"
              dependon={"id_parent"}
            />
            <Radio
              name="type_indice"
              value="ILAT"
              label="ILAT"
              dependon={"id_parent"}
            />
            <Radio
              name="type_indice"
              value="ICC"
              label="ICC"
              dependon={"id_parent"}
            />
            {values.type === "commercial" && (
              <Radio
                name="type_indice"
                value="Aucun"
                label="Aucun"
                dependon={"id_parent"}
              />
            )}
          </div>
        </>
      )}
      {values.zone_tendue === "true" && (
        <ZoneTendue loyerReference={values.loyer_reference === "true"} />
      )}
    </>
  );
}

export default Finance;
